// pages/TrainingPage.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import trainingController from '../../controllers/trainingController';
import Training from '../../models/Training';
import { 
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, ChevronUp, ChevronDown,
  GraduationCap, Users, Calendar, MapPin, Video, Clock, DollarSign, CheckCircle, Link2
} from 'lucide-react';

const TrainingPage = () => {
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [filterMode, setFilterMode] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [formData, setFormData] = useState({
    trainingName: '',
    trainingType: 'Technical',
    trainer: '',
    description: '',
    startDate: '',
    endDate: '',
    duration: '',
    location: '',
    mode: 'Offline',
    capacity: 0,
    cost: 0,
    status: 'Planned'
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('startDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const trainingTypes = ['Technical', 'Soft Skills', 'Leadership', 'Compliance', 'Safety', 'Product', 'Other'];
  const modes = ['Offline', 'Online', 'Hybrid'];

  useEffect(() => {
    fetchTrainings();
  }, []);

  useEffect(() => {
    let result = [...trainings];
    
    if (searchQuery) {
      result = result.filter(t =>
        t.trainingName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.trainer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.location?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (filterStatus !== 'All') {
      result = result.filter(t => t.status === filterStatus);
    }
    
    if (filterType !== 'All') {
      result = result.filter(t => t.trainingType === filterType);
    }
    
    if (filterMode !== 'All') {
      result = result.filter(t => t.mode === filterMode);
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredTrainings(result);
    setCurrentPage(1);
  }, [trainings, searchQuery, filterStatus, filterType, filterMode, sortField, sortDirection]);

  const fetchTrainings = async () => {
    setLoading(true);
    const result = await trainingController.getAll({ page: 1, limit: 100 });
    if (result.success) {
      setTrainings(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' ? parseInt(value) || 0 : value;
    
    setFormData(prev => {
      const updated = { ...prev, [name]: newValue };
      
      // Auto-calculate duration when dates change
      if (name === 'startDate' || name === 'endDate') {
        if (updated.startDate && updated.endDate) {
          const start = new Date(updated.startDate);
          const end = new Date(updated.endDate);
          const diffTime = Math.abs(end - start);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          updated.duration = `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
        }
      }
      
      return updated;
    });
    
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({
      trainingName: '',
      trainingType: 'Technical',
      trainer: '',
      description: '',
      startDate: '',
      endDate: '',
      duration: '',
      location: '',
      mode: 'Offline',
      capacity: 0,
      cost: 0,
      status: 'Planned'
    });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);

    const result = await trainingController.create(formData);
    
    if (result.success) {
      setShowAddModal(false);
      fetchTrainings();
      alert('✅ Training created successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    
    setFormSubmitting(false);
  };

  const handleEdit = (training) => {
    setSelectedTraining(training);
    setFormData({
      trainingName: training.trainingName,
      trainingType: training.trainingType,
      trainer: training.trainer,
      description: training.description,
      startDate: training.startDate,
      endDate: training.endDate,
      duration: training.duration,
      location: training.location,
      mode: training.mode,
      capacity: training.capacity,
      cost: training.cost,
      status: training.status
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);

    const result = await trainingController.update(selectedTraining.id, formData);
    
    if (result.success) {
      setShowEditModal(false);
      fetchTrainings();
      alert('✅ Training updated successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    
    setFormSubmitting(false);
  };

  const handleView = (training) => {
    setSelectedTraining(training);
    setShowViewModal(true);
  };

  const handleDelete = async (training) => {
    const isConfirmed = window.confirm(`Delete training: ${training.trainingName}?`);
    if (!isConfirmed) return;

    const result = await trainingController.delete(training.id);
    
    if (result.success) {
      fetchTrainings();
      alert('✅ Training deleted successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleJoinMeeting = (training) => {
    // Generate or use existing room ID
    const roomId = training.meetingRoomId || `room-${training.id}`;
    navigate(`/meeting/${training.id}/${roomId}`);
  };

  const handleCopyMeetingLink = (training) => {
    const roomId = training.meetingRoomId || `room-${training.id}`;
    const link = `${window.location.origin}/meeting/${training.id}/${roomId}`;
    navigator.clipboard.writeText(link);
    alert('✅ Meeting link copied to clipboard!');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const result = await trainingController.export({ status: filterStatus, trainingType: filterType }, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trainings-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Trainings exported!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTrainings = filteredTrainings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTrainings.length / itemsPerPage);

  const stats = {
    total: trainings.length,
    planned: trainings.filter(t => t.status === 'Planned').length,
    ongoing: trainings.filter(t => t.status === 'Ongoing').length,
    completed: trainings.filter(t => t.status === 'Completed').length,
    totalEnrolled: trainings.reduce((sum, t) => sum + t.enrolled, 0),
    totalCost: trainings.reduce((sum, t) => sum + t.cost, 0)
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const styles = {
    container: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' },
    secondaryButton: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' },
    select: { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
    actionButton: { padding: '6px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', transition: 'background-color 0.2s' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #e2e8f0' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' },
    textarea: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', resize: 'vertical', minHeight: '100px', fontFamily: 'inherit' },
    errorList: { backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' },
    errorItem: { color: '#dc2626', fontSize: '14px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' },
    modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Training Management</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={handleExport}>
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Add Training
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Programs</div>
            <GraduationCap size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Planned</div>
            <Calendar size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.planned}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Ongoing</div>
            <Clock size={24} color="#3b82f6" />
          </div>
          <div style={{ ...styles.statValue, color: '#3b82f6' }}>{stats.ongoing}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Completed</div>
            <CheckCircle size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.completed}</div>
        </div>

        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Enrolled</div>
            <Users size={24} color="#8b5cf6" />
          </div>
          <div style={{ ...styles.statValue, color: '#8b5cf6' }}>{stats.totalEnrolled}</div>
        </div>

        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Investment</div>
            <DollarSign size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, fontSize: '20px', color: '#10b981' }}>
            {formatCurrency(stats.totalCost)}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search trainings..." 
            style={styles.searchInput} 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
        
        <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Planned">Planned</option>
          <option value="Ongoing">Ongoing</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
        </select>
        
        <select style={styles.select} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="All">All Types</option>
          {trainingTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        <select style={styles.select} value={filterMode} onChange={(e) => setFilterMode(e.target.value)}>
          <option value="All">All Modes</option>
          {modes.map(mode => (
            <option key={mode} value={mode}>{mode}</option>
          ))}
        </select>
      </div>

      {/* Trainings Table */}
      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            Loading trainings...
          </div>
        ) : currentTrainings.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            No trainings found. Click "Add Training" to create one.
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} onClick={() => handleSort('trainingName')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Training Name
                      {sortField === 'trainingName' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Type & Mode</th>
                  <th style={styles.th}>Trainer</th>
                  <th style={styles.th} onClick={() => handleSort('startDate')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Schedule
                      {sortField === 'startDate' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Enrollment</th>
                  <th style={styles.th}>Cost</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentTrainings.map((training) => {
                  const availableSeats = training.capacity - training.enrolled;
                  const isOnline = training.mode === 'Online' || training.mode === 'Hybrid';
                  
                  return (
                    <tr key={training.id} style={{ cursor: 'pointer' }} onClick={() => handleView(training)}>
                      <td style={styles.td}>
                        <div style={{ fontWeight: '600' }}>{training.trainingName}</div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                          <MapPin size={12} style={{ display: 'inline', marginRight: '4px' }} />
                          {training.location || 'TBA'}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ ...styles.badge, backgroundColor: '#eff6ff', color: '#3b82f6', marginBottom: '4px' }}>
                          {training.trainingType}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {training.mode === 'Online' ? <Video size={12} style={{ display: 'inline', marginRight: '4px' }} /> : <MapPin size={12} style={{ display: 'inline', marginRight: '4px' }} />}
                          {training.mode}
                        </div>
                      </td>
                      <td style={styles.td}>{training.trainer}</td>
                      <td style={styles.td}>
                        <div style={{ fontWeight: '500', fontSize: '13px' }}>
                          {formatDate(training.startDate)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          to {formatDate(training.endDate)}
                        </div>
                        {training.duration && (
                          <div style={{ fontSize: '11px', color: '#8b5cf6', marginTop: '2px' }}>
                            {training.duration}
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontWeight: '600', color: '#3b82f6' }}>
                          {training.enrolled}/{training.capacity}
                        </div>
                        <div style={{ fontSize: '11px', color: availableSeats === 0 ? '#ef4444' : '#64748b', marginTop: '2px' }}>
                          {availableSeats === 0 ? 'Full' : `${availableSeats} seats left`}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontWeight: '600', color: '#10b981' }}>
                          {formatCurrency(training.cost)}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, backgroundColor: `${training.getStatusColor()}20`, color: training.getStatusColor() }}>
                          {training.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {isOnline && (
                            <button style={{ ...styles.actionButton, backgroundColor: '#10b981' }} onClick={(e) => { e.stopPropagation(); handleJoinMeeting(training); }} title="Join Meeting">
                              <Video size={18} color="#fff" />
                            </button>
                          )}
                          {isOnline && (
                            <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleCopyMeetingLink(training); }} title="Copy Meeting Link">
                              <Link2 size={18} color="#8b5cf6" />
                            </button>
                          )}
                          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleView(training); }} title="View">
                            <Eye size={18} color="#64748b" />
                          </button>
                          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleEdit(training); }} title="Edit">
                            <Edit2 size={18} color="#3b82f6" />
                          </button>
                          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleDelete(training); }} title="Delete">
                            <Trash2 size={18} color="#ef4444" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredTrainings.length)} of {filteredTrainings.length} trainings
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modals remain the same as in original code... */}
      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{showAddModal ? 'Create New Training' : 'Edit Training'}</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Training Name *</label>
                    <input type="text" name="trainingName" style={styles.input} value={formData.trainingName} onChange={handleInputChange} required placeholder="e.g. Advanced React Development" />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Training Type *</label>
                    <select name="trainingType" style={styles.input} value={formData.trainingType} onChange={handleInputChange}>
                      {trainingTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Trainer *</label>
                    <input type="text" name="trainer" style={styles.input} value={formData.trainer} onChange={handleInputChange} required placeholder="Trainer name" />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Start Date *</label>
                    <input type="date" name="startDate" style={styles.input} value={formData.startDate} onChange={handleInputChange} required min={new Date().toISOString().split('T')[0]} />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>End Date *</label>
                    <input type="date" name="endDate" style={styles.input} value={formData.endDate} onChange={handleInputChange} required min={formData.startDate || new Date().toISOString().split('T')[0]} />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Duration (Auto-calculated)</label>
                    <input type="text" name="duration" style={{ ...styles.input, backgroundColor: '#f8fafc' }} value={formData.duration} readOnly placeholder="Auto-calculated" />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Mode</label>
                    <select name="mode" style={styles.input} value={formData.mode} onChange={handleInputChange}>
                      {modes.map(mode => (
                        <option key={mode} value={mode}>{mode}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Location</label>
                    <input type="text" name="location" style={styles.input} value={formData.location} onChange={handleInputChange} placeholder="Office, Online, or City" />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Capacity *</label>
                    <input type="number" name="capacity" style={styles.input} value={formData.capacity} onChange={handleInputChange} required min="1" placeholder="Maximum participants" />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Cost (₹)</label>
                    <input type="number" name="cost" style={styles.input} value={formData.cost} onChange={handleInputChange} min="0" placeholder="Training cost" />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select name="status" style={styles.input} value={formData.status} onChange={handleInputChange}>
                      <option value="Planned">Planned</option>
                      <option value="Ongoing">Ongoing</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Description</label>
                    <textarea name="description" style={{ ...styles.textarea, minHeight: '120px' }} value={formData.description} onChange={handleInputChange} placeholder="Training objectives, topics covered, learning outcomes..."></textarea>
                  </div>
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }} disabled={formSubmitting}>
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Create Training' : 'Update Training')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedTraining && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{selectedTraining.trainingName}</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => setShowViewModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Training Type</div>
                  <div style={{ ...styles.badge, backgroundColor: '#eff6ff', color: '#3b82f6' }}>{selectedTraining.trainingType}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{ ...styles.badge, backgroundColor: `${selectedTraining.getStatusColor()}20`, color: selectedTraining.getStatusColor() }}>
                    {selectedTraining.status}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Trainer</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedTraining.trainer}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Mode</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {selectedTraining.mode === 'Online' ? <Video size={16} color="#3b82f6" /> : <MapPin size={16} color="#3b82f6" />}
                    {selectedTraining.mode}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Schedule</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>
                    {formatDate(selectedTraining.startDate)} - {formatDate(selectedTraining.endDate)}
                  </div>
                  {selectedTraining.duration && (
                    <div style={{ fontSize: '13px', color: '#8b5cf6', marginTop: '2px' }}>
                      {selectedTraining.duration}
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Location</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MapPin size={16} color="#10b981" />
                    {selectedTraining.location || 'TBA'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Enrollment</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#3b82f6' }}>
                    {selectedTraining.enrolled} / {selectedTraining.capacity}
                  </div>
                  <div style={{ fontSize: '13px', color: selectedTraining.capacity - selectedTraining.enrolled === 0 ? '#ef4444' : '#10b981', marginTop: '2px' }}>
                    {selectedTraining.capacity - selectedTraining.enrolled === 0 ? 'Full' : `${selectedTraining.capacity - selectedTraining.enrolled} seats available`}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Cost</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#10b981' }}>
                    {formatCurrency(selectedTraining.cost)}
                  </div>
                </div>
              </div>

              {selectedTraining.description && (
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>Description</div>
                  <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                    {selectedTraining.description}
                  </div>
                </div>
              )}

              {(selectedTraining.mode === 'Online' || selectedTraining.mode === 'Hybrid') && (
                <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#eff6ff', borderRadius: '8px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#1e293b' }}>
                    Online Meeting
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button 
                      onClick={() => handleJoinMeeting(selectedTraining)} 
                      style={{ ...styles.button, backgroundColor: '#10b981' }}
                    >
                      <Video size={18} /> Join Meeting
                    </button>
                    <button 
                      onClick={() => handleCopyMeetingLink(selectedTraining)} 
                      style={{ ...styles.button, ...styles.secondaryButton }}
                    >
                      <Link2 size={18} /> Copy Link
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowViewModal(false); handleEdit(selectedTraining); }}>
                <Edit2 size={16} /> Edit Training
              </button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingPage;